import React, { useEffect, useState } from "react";
import { Plus, Trash2, ChevronDown, ChevronRight, CheckCircle2, Circle, CircleDashed } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/api";

const TYPES = [
  { value: "Mojo", color: "bg-emerald-50 text-emerald-700 border-emerald-200", active: "bg-emerald-600 text-white border-emerald-600" },
  { value: "Kerboom", color: "bg-orange-50 text-orange-700 border-orange-200", active: "bg-orange-600 text-white border-orange-600" },
  { value: "Personal", color: "bg-violet-50 text-violet-700 border-violet-200", active: "bg-violet-600 text-white border-violet-600" },
];

const TYPE_LOOKUP = TYPES.reduce((m, t) => { m[t.value] = t; return m; }, {});

// Status cycle: Todo -> In Progress -> Complete -> Todo
const NEXT_STATUS = { "Todo": "In Progress", "In Progress": "Complete", "Complete": "Todo" };

const StatusBadge = ({ status, onClick }) => {
  const styles = {
    "Todo": "bg-rose-100 text-rose-800 hover:bg-rose-200",
    "In Progress": "bg-amber-100 text-amber-800 hover:bg-amber-200",
    "Complete": "bg-zinc-100 text-zinc-600 hover:bg-zinc-200",
  };
  const Icon = status === "Complete" ? CheckCircle2 : status === "In Progress" ? CircleDashed : Circle;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full transition-colors ${styles[status]}`}
      title="Click to advance status"
      data-testid={`todo-status-${status.replace(/\s/g, '-').toLowerCase()}`}
    >
      <Icon className="w-3 h-3" />
      {status}
    </button>
  );
};

const UrgencyDots = ({ value, onChange, readOnly = false }) => (
  <div className="flex items-center gap-0.5" data-testid="todo-urgency">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        disabled={readOnly}
        onClick={() => onChange?.(n)}
        className={`w-2 h-2 rounded-full transition-colors ${
          n <= value ? "bg-orange-500" : "bg-zinc-300"
        } ${!readOnly ? "hover:scale-125 cursor-pointer" : ""}`}
        title={`Urgency ${n}`}
        data-testid={`todo-urgency-${n}`}
      />
    ))}
  </div>
);

const TodoRow = ({ todo, onUpdate, onDelete }) => {
  const t = TYPE_LOOKUP[todo.type] || TYPE_LOOKUP.Kerboom;
  const cycleStatus = () => onUpdate({ ...todo, status: NEXT_STATUS[todo.status] || "Todo" });
  return (
    <div className="group flex items-center gap-3 px-3 py-2 border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`todo-row-${todo.id}`}>
      <p title={todo.title} className={`flex-1 min-w-0 text-sm font-semibold leading-snug truncate ${todo.status === "Complete" ? "line-through text-zinc-400 font-normal" : "text-zinc-900"}`}>
        {todo.title}
      </p>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`inline-block text-[10px] font-medium px-2 py-0.5 rounded-full border ${t.color}`}>{todo.type}</span>
        <UrgencyDots value={todo.urgency} onChange={(n) => onUpdate({ ...todo, urgency: n })} />
        <StatusBadge status={todo.status} onClick={cycleStatus} />
        <button
          type="button"
          onClick={() => onDelete(todo.id)}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded text-zinc-400 hover:text-red-600 hover:bg-red-50"
          title="Delete"
          data-testid={`todo-delete-${todo.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

const TodoPanel = ({ open, onOpenChange, trigger }) => {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newType, setNewType] = useState("Kerboom");
  const [newUrgency, setNewUrgency] = useState(3);
  const [adding, setAdding] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/todos");
      setTodos(data || []);
    } catch {
      // ignore — sidebar trigger may render before auth completes; just stay empty
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const submitNew = async (e) => {
    e.preventDefault();
    const title = newTitle.trim();
    if (!title) return;
    setAdding(true);
    try {
      const { data } = await api.post("/todos", { title, type: newType, urgency: newUrgency, status: "Todo" });
      setTodos((arr) => [data, ...arr]);
      setNewTitle("");
      setNewUrgency(3);
      // re-sort via server next refresh
      load();
    } finally {
      setAdding(false);
    }
  };

  const updateTodo = async (todo) => {
    // Optimistic
    setTodos((arr) => arr.map((t) => (t.id === todo.id ? { ...t, ...todo } : t)));
    try {
      await api.put(`/todos/${todo.id}`, {
        title: todo.title,
        type: todo.type,
        urgency: todo.urgency,
        status: todo.status,
      });
      load();
    } catch {
      load();
    }
  };

  const deleteTodo = async (id) => {
    setTodos((arr) => arr.filter((t) => t.id !== id));
    try { await api.delete(`/todos/${id}`); } catch {}
  };

  const open_todos = todos.filter((t) => t.status === "Todo");
  const in_progress = todos.filter((t) => t.status === "In Progress");
  const completed = todos.filter((t) => t.status === "Complete");

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        sideOffset={8}
        className="w-[520px] p-0 flex flex-col max-h-[75vh] overflow-hidden rounded-2xl shadow-2xl"
        data-testid="todo-panel"
      >
        <div className="px-5 pt-5 pb-3 border-b border-zinc-200">
          <h3 className="font-display text-2xl text-zinc-950">To-Do</h3>
          <p className="text-xs text-zinc-500 mt-0.5">Quick reminders for things outside your projects.</p>
        </div>

        {/* Add form */}
        <form onSubmit={submitNew} className="px-5 py-4 border-b border-zinc-200 bg-zinc-50/50" data-testid="todo-add-form">
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="What needs doing?"
              className="flex-1 px-3 py-2 text-sm border border-zinc-200 rounded-lg bg-white focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950 outline-none"
              data-testid="todo-new-title"
            />
            <button
              type="submit"
              disabled={adding || !newTitle.trim()}
              className="px-3 py-2 rounded-lg bg-zinc-950 text-white text-sm font-semibold hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="todo-new-submit"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setNewType(t.value)}
                className={`text-[11px] font-medium px-2.5 py-1 rounded-full border transition-colors ${newType === t.value ? t.active : t.color}`}
                data-testid={`todo-new-type-${t.value}`}
              >
                {t.value}
              </button>
            ))}
            <span className="text-[10px] text-zinc-400 ml-auto mr-1">Urgency:</span>
            <UrgencyDots value={newUrgency} onChange={setNewUrgency} />
          </div>
        </form>

        {/* Lists */}
        <div className="flex-1 overflow-y-auto">
          {loading && todos.length === 0 ? (
            <p className="text-sm text-zinc-400 px-5 py-8 text-center">Loading…</p>
          ) : (
            <>
              <Section label="To-Do" count={open_todos.length}>
                {open_todos.length === 0 ? (
                  <p className="text-xs text-zinc-400 px-5 py-3">Nothing pending. Nice.</p>
                ) : (
                  open_todos.map((t) => <TodoRow key={t.id} todo={t} onUpdate={updateTodo} onDelete={deleteTodo} />)
                )}
              </Section>

              <Section label="In Progress" count={in_progress.length}>
                {in_progress.length === 0 ? (
                  <p className="text-xs text-zinc-400 px-5 py-3">Nothing in flight.</p>
                ) : (
                  in_progress.map((t) => <TodoRow key={t.id} todo={t} onUpdate={updateTodo} onDelete={deleteTodo} />)
                )}
              </Section>

              {/* Completed - collapsed by default */}
              <div className="border-t border-zinc-200" data-testid="todo-completed-section">
                <button
                  type="button"
                  onClick={() => setShowCompleted((v) => !v)}
                  className="w-full px-5 py-3 flex items-center justify-between text-xs font-semibold text-zinc-600 hover:bg-zinc-50"
                  data-testid="todo-toggle-completed"
                >
                  <span className="flex items-center gap-1.5">
                    {showCompleted ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    Completed
                  </span>
                  <span className="text-zinc-400 font-normal">{completed.length}</span>
                </button>
                {showCompleted && (
                  completed.length === 0 ? (
                    <p className="text-xs text-zinc-400 px-5 py-3">None yet.</p>
                  ) : (
                    completed.map((t) => <TodoRow key={t.id} todo={t} onUpdate={updateTodo} onDelete={deleteTodo} />)
                  )
                )}
              </div>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const Section = ({ label, count, children }) => (
  <div>
    <div className="px-5 py-2 bg-zinc-50/70 border-b border-zinc-100 flex items-center justify-between">
      <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-700">{label}</span>
      <span className="text-[10px] font-medium text-zinc-500 bg-white border border-zinc-200 px-1.5 rounded-full">{count}</span>
    </div>
    {children}
  </div>
);

export default TodoPanel;
